import React, { useEffect, useRef } from 'react';
import * as styles from './Table.module.css';

const ROW_CLICK_DRAG_THRESHOLD_PX = 5;

const hasTextSelection = () => {
  const selection = window.getSelection();
  return Boolean(
    selection && selection.type === 'Range' && selection.toString().trim()
  );
};

const isRowDragClick = (
  event: React.MouseEvent<HTMLTableRowElement>,
  pointerDown: { x: number; y: number } | null
) => {
  if (!pointerDown) {
    return false;
  }

  const dx = Math.abs(event.clientX - pointerDown.x);
  const dy = Math.abs(event.clientY - pointerDown.y);

  return dx > ROW_CLICK_DRAG_THRESHOLD_PX || dy > ROW_CLICK_DRAG_THRESHOLD_PX;
};

export interface TableColumn<T = unknown> {
  key: string;
  title: string;
  width?: string;
  sticky?: boolean;
  render?: (value: unknown, item: T, index: number) => React.ReactNode;
  renderHeader?: () => React.ReactNode;
  className?: string;
  sortable?: boolean;
  onSort?: () => void;
  split?: boolean;
}

export interface TableProps<T = unknown> {
  columns: TableColumn<T>[];
  data: T[];
  loading?: boolean;
  error?: string | null;
  emptyMessage?: string;
  stickyHeader?: boolean;
  skeletonRows?: number;
  className?: string;
  rowKey?: keyof T | ((item: T, index: number) => string | number);
  onRowClick?: (
    item: T,
    index: number,
    event: React.MouseEvent<HTMLTableRowElement>
  ) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isFetchingMore?: boolean;
}

export const Table = <T,>({
  columns,
  data,
  loading = false,
  error = null,
  emptyMessage = 'Нет данных для отображения',
  stickyHeader = true,
  skeletonRows = 10,
  className = '',
  rowKey = (item: T, index: number) => index,
  onLoadMore,
  hasMore,
  isFetchingMore,
  onRowClick,
}: TableProps<T>) => {
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const rowPointerDownRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (!onLoadMore || !hasMore) return;

    const root = scrollContainerRef.current;
    const target = sentinelRef.current;

    if (!root || !target) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !isFetchingMore) {
          onLoadMore();
        }
      },
      {
        root: root,
        rootMargin: '100px',
        threshold: 0.1,
      }
    );

    observer.observe(target);

    return () => observer.disconnect();
  }, [onLoadMore, hasMore, isFetchingMore]);

  const getRowKey = (item: T, index: number): string | number => {
    if (typeof rowKey === 'function') {
      return rowKey(item, index);
    }
    return (
      ((item as Record<string, unknown>)[rowKey as string] as
        | string
        | number) || index
    );
  };

  const renderCell = (column: TableColumn<T>, item: T, index: number) => {
    if (column.render) {
      const value = (item as Record<string, unknown>)[column.key];
      return column.render(value, item, index);
    }
    return ((item as Record<string, unknown>)[column.key] as string) || '—';
  };

  const handleRowClick = (
    event: React.MouseEvent<HTMLTableRowElement>,
    item: T,
    index: number
  ) => {
    if (!onRowClick) {
      return;
    }

    if (
      hasTextSelection() ||
      isRowDragClick(event, rowPointerDownRef.current)
    ) {
      return;
    }

    onRowClick(item, index, event);
  };

  const renderTable = (isSticky = false) => (
    <table className={`${styles.table} ${isSticky ? styles.stickyTable : ''}`}>
      <colgroup>
        {columns.map(column => (
          <col key={column.key} style={{ width: column.width }} />
        ))}
      </colgroup>
      <thead className={styles.tableHead}>
        <tr>
          {columns.map(column => (
            <th
              key={column.key}
              className={`${styles.headerCell} ${
                column.sticky ? styles.headerCellSticky : ''
              } ${column.className || ''} ${
                column.sortable ? styles.sortableHeader : ''
              }`}
              onClick={
                column.sortable && column.onSort ? column.onSort : undefined
              }
              style={{
                cursor: column.sortable ? 'pointer' : 'default',
              }}
            >
              {column.renderHeader ? column.renderHeader() : column.title}
            </th>
          ))}
        </tr>
      </thead>
      {!isSticky && (
        <tbody>
          {data.map((item, index) => (
            <tr
              key={getRowKey(item, index)}
              className={`${styles.row} ${onRowClick ? styles.clickableRow : ''}`}
              onMouseDown={event => {
                rowPointerDownRef.current = {
                  x: event.clientX,
                  y: event.clientY,
                };
              }}
              onClick={event => handleRowClick(event, item, index)}
            >
              {columns.map(column => {
                const splitCols = columns.filter(c => c.split);

                const groupRows = Math.max(
                  1,
                  ...splitCols.map(col => {
                    const v = (item as Record<string, unknown>)[col.key];
                    return Array.isArray(v) ? v.length : 1;
                  })
                );
                if (!column.split) {
                  return (
                    <td
                      key={column.key}
                      className={`${styles.cell} ${
                        column.sticky ? styles.cellSticky : ''
                      } ${column.className || ''}`}
                    >
                      {renderCell(column, item, index)}
                    </td>
                  );
                }
                const v = (item as Record<string, unknown>)[column.key];
                const arr = Array.isArray(v) ? v : v == null ? [] : [v];
                return (
                  <td
                    key={column.key}
                    className={`${styles.cell} ${
                      column.sticky ? styles.cellSticky : ''
                    } ${column.className || ''}`}
                  >
                    <div
                      className={styles.splitGrid}
                      style={{
                        gridTemplateRows: `repeat(${groupRows}, minmax(0, 1fr))`,
                      }}
                    >
                      {Array.from({ length: groupRows }).map((_, i) => (
                        <div key={i} className={styles.splitRow}>
                          {column.render
                            ? column.render(arr[i], item, index)
                            : (arr[i] ?? '')}
                        </div>
                      ))}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      )}
    </table>
  );

  if (error) {
    return <div className={styles.error}>Ошибка: {error}</div>;
  }

  if (loading) {
    return (
      <div className={`${styles.tableWrapper} ${className}`}>
        <table className={styles.table}>
          <colgroup>
            {columns.map(column => (
              <col key={column.key} style={{ width: column.width }} />
            ))}
          </colgroup>
          <thead className={styles.tableHead}>
            <tr>
              {columns.map(column => (
                <th
                  key={column.key}
                  className={`${styles.headerCell} ${
                    column.sticky ? styles.headerCellSticky : ''
                  } ${column.className || ''} ${
                    column.sortable ? styles.sortableHeader : ''
                  }`}
                  onClick={
                    column.sortable && column.onSort ? column.onSort : undefined
                  }
                  style={{
                    cursor: column.sortable ? 'pointer' : 'default',
                  }}
                >
                  {column.renderHeader ? column.renderHeader() : column.title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: skeletonRows }).map((_, index) => (
              <tr key={index} className={styles.row}>
                {columns.map(column => (
                  <td
                    key={column.key}
                    className={`${styles.cell} ${
                      column.sticky ? styles.cellSticky : ''
                    } ${column.className || ''}`}
                  >
                    <div className={styles.skeleton} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div
        className={`${styles.tableWrapper} ${stickyHeader ? styles.withStickyHeader : ''} ${className}`}
      >
        <div className={styles.tableScrollContainer}>
          <table className={styles.table}>
            <colgroup>
              {columns.map(column => (
                <col key={column.key} style={{ width: column.width }} />
              ))}
            </colgroup>
            <thead className={styles.tableHead}>
              <tr>
                {columns.map(column => (
                  <th
                    key={column.key}
                    className={`${styles.headerCell} ${
                      column.sticky ? styles.headerCellSticky : ''
                    } ${column.className || ''}`}
                  >
                    {column.renderHeader ? column.renderHeader() : column.title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colSpan={columns.length} className={styles.emptyCell}>
                  {emptyMessage}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={tableWrapperRef}
      className={`${styles.tableWrapper} ${stickyHeader ? styles.withStickyHeader : ''} ${className}`}
    >
      <div ref={scrollContainerRef} className={styles.tableScrollContainer}>
        {renderTable()}

        {hasMore && <div ref={sentinelRef} style={{ height: 1 }} />}
      </div>
    </div>
  );
};

export default Table;
